// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

export const zhCN = {
  app: { title: "Cybertron 设备档案", subtitle: "设备档案系统" },
  login: {
    sso: "使用公司账号登录",
    hint: "本系统不保存密码，登录由公司统一身份认证完成。",
    retry: "重新登录",
    errors: {
      DOMAIN_NOT_ALLOWED:
        "只能使用 dynarobotics.ai 公司账号登录。请在 Google 账号选择页换成公司账号。",
      EMAIL_NOT_VERIFIED: "这个 Google 账号的邮箱尚未验证，无法登录。",
      ACCOUNT_DISABLED: "该账号已被停用，请联系管理员。",
      LOGIN_STATE_INVALID: "这次登录已过期或已使用过（例如刷新了登录页面）。请点下方按钮重新登录。",
      LOGIN_FAILED: "登录没有完成（可能取消了授权，或暂时连不上 Google）。请重新登录。",
      unknown: "登录失败（{{code}}），请重新登录。",
    },
  },
  nav: {
    archives: "档案查询",
    productTypes: "设备类型",
    boms: "BOM 管理",
    workbench: "新建档案",
    archiveImport: "批量建档",
    audit: "审计日志",
    users: "用户管理",
    switchUser: "切换用户",
    signOut: "注销",
    accountMenu: "账号菜单",
    system: "系统信息",
    group: { work: "档案管理", masterData: "主数据", system: "系统" },
  },
  roles: {
    ADMIN: "管理员",
    ENGINEER: "维护工程师",
    VISITOR: "访客",
    null: "",
    noAccess: "当前账号（{{role}}）无权使用此页面，请联系管理员。",
    readOnly: "访客只能查看，不能修改",
  },
  users: {
    title: "用户管理",
    hint: "登录过的账号才会出现在这里：新账号首次登录为维护工程师。管理员可管理设备类型、BOM、审计日志和用户；维护工程师可建档、记录变更和报废；访客只能查看。",
    email: "邮箱",
    name: "姓名",
    role: "角色",
    lastLogin: "最近登录",
    you: "（你）",
    roleOf: "{{email}} 的角色",
  },
  common: {
    next: "下一步",
    back: "上一步",
    save: "保存",
    cancel: "取消",
    submit: "提交",
    copied: "已复制",
    copyLink: "复制链接",
    copyFailed: "复制失败：浏览器不允许访问剪贴板",
    create: "新建",
    edit: "编辑",
    delete: "删除",
    search: "搜索",
    export: "导出 CSV",
    loading: "加载中…",
    empty: "暂无数据",
    more: "加载更多",
    confirm: "确认",
    yes: "是",
    no: "否",
    refresh: "刷新",
    close: "关闭",
    retry: "重试",
    required: "必填",
  },
  productType: {
    title: "设备类型",
    name: "名称",
    hasBom: "使用 BOM",
    archivePrefix: "档案号前缀",
    archivePrefixHint:
      "2–8 位大写字母或数字，首位必须是字母（如 RBT、SM8B4）。它会出现在这个类型今后每一个档案号的开头，创建后不可修改。",
    deviceNoRegex: "设备编号规则（正则）",
    status: "状态",
    bomCount: "BOM 数",
    archiveCount: "档案数",
    discontinue: "停产",
    activate: "重新启用",
    discontinueHint:
      "停产后不能再新建档案，该类型下的 BOM 会全部停用。重新启用类型不会自动恢复这些 BOM。",
    createTitle: "新建设备类型",
    immutableHint: "设备类型创建后不可修改，只能停产。档案号前缀会写进这个类型今后每一个档案号里。",
    deleteTitle: "删除设备类型",
    deleteConfirm: "确认删除「{{name}}」？此操作不可撤销。",
    deleteBoms: "该类型下 {{boms}} 个未使用的 BOM 会一并删除。",
    deleteBlocked:
      "「{{name}}」下已有 {{archives}} 个档案，不能删除。改为停产即可停止新建档案，已有档案仍然可查。",
  },
  archiveImport: {
    moduleHint:
      "「模组标签」列每个模组一个标签，「模组SN」列填模组标签上的 SN（必填）；每个关键槽位和下层模组位一行。",
    needsModuleType: "模组批量需先选择设备类型、BOM 和模组类型",
    label: "模组标签",
    downloadResult: "下载结果",
    title: "批量建档",
    defaultBom: "BOM 版本",
    bomFromFile: "BOM 版本：按文件内指定",
    stepsLabel: "批量建档步骤",
    step1: "1 选择设备类型和 BOM 版本",
    step2: "2 下载模板并填写",
    step3: "3 上传文件，自动预检",
    step4: "4 预检通过后提交",
    chooseFile: "上传文件",
    needsType: "先选择设备类型，再上传文件。模板不分类型，随时可以下载。",
    hint: "一行一个序列号，一台设备占多行；「整机SN」列必填，每行填设备标签上的 SN。上传后先预检，确认无误再提交；一个批次全成全败。",
    reportTitle: "预检结果",
    passed: "{{devices}} 台设备，{{serials}} 个序列号，可以提交。",
    failed: "{{count}} 个问题，全部修好后才能提交 —— 批次不支持只导入没问题的设备。",
    issueDetail: "详情",
    commit: "提交建档",
    filedTitle: "已建档 {{count}} 台",
  },
  barcodeRules: {
    title: "物料 {{material}} 的条码规则",
    shared:
      "本 BOM 中 {{n}} 个部位使用该物料（{{lines}}），共用这些规则；其他 BOM 中的同一物料也共用。",
    anyMatches:
      "扫码时序列号符合任意一条规则即可录入；没有规则时不校验。发布后的 BOM 也可以添加或修改。",
    pattern: "规则（正则）",
    example: "示例 SN",
    examplePlaceholder: "扫入或输入一个真实的序列号",
    none: "还没有规则，任何序列号都可以录入",
    add: "添加规则",
    addTitle: "添加规则",
    editTitle: "修改规则",
    edit: "修改规则 {{pattern}}",
    delete: "删除规则 {{pattern}}",
    regenerate: "按示例重新生成",
    generatedHint:
      "规则按示例自动生成：连续数字匹配同样位数的数字，字母和符号保持不变。如有字母会变化，可直接修改规则。",
    invalid: "规则不是有效的正则表达式",
    acceptsExample: "规则匹配示例 SN",
    rejectsExample: "规则不匹配示例 SN，无法保存",
    trial: "试一个序列号",
    trialMatches: "符合规则",
    trialFails: "不符合规则",
  },
  bom: {
    title: "BOM 管理",
    version: "版本",
    status: "状态",
    rows: "行数",
    criticalItems: "关键件行数",
    requiredSn: "需录 SN 数",
    archiveCount: "档案数",
    disable: "停用",
    enable: "启用",
    disabledHint:
      "该 BOM 已停用：不能再用于建档，也不能发布。已建的档案不受影响；点「启用」可恢复。",
    deleteBlocked: "已有 {{count}} 个档案照这个版本建档，不能删除；可以停用。",
    deleteTitle: "删除 BOM",
    deleteDraftConfirm: "确认删除草稿 {{version}}？它的 {{rows}} 行会一并删除，此操作不可撤销。",
    deleteConfirm: "确认删除 BOM {{version}}？它的全部行会一并删除，此操作不可撤销。",
    publish: "发布",
    publishTitle: "发布 BOM",
    unnumbered: "未填写",
    setVersion: "设置版本号",
    versionPlaceholder: "如 1.5",
    versionHint:
      "1.0 至 9.99，最多两位小数；版本号即 SN 前三位（1.5 → 150，2.2 → 220）。同一机型用过的版本号不能再给其他 BOM，删掉的也不行。留空可清除。",
    publishConfirm:
      "发布后除备注外不可修改，要改只能派生新版本。当前 {{critical}} 个关键件行，展开后建档需录 {{slots}} 个序列号。名称可留空。",
    derive: "派生新版本",
    newDraft: "新建草稿",
    name: "名称",
    namePlaceholder: "如 量产版",
    import: "导入文件",
    downloadTemplate: "下载模板",
    importNeedsType: "导入前先选择设备类型——文件要按这个类型校验。模板可以随时下载。",
    templateHint:
      "模板为工程 BOM 格式，标黄的五列必填；层级用「父级件料号 → 子级件料号」表示，「单位用量」填整机用量。第二个工作表有填写说明，BOM 表里有一份示例。旧的位置码格式文件仍可导入。",
    positionCode: "位置码",
    positionName: "物料描述",
    partName: "部位名称",
    partNamePlaceholder: "留空则沿用父行的部位名称",
    noModule: "无（不是模组）",
    drawingNo: "图号",
    quantity: "单位用量",
    effectiveQuantity: "整机用量",
    critical: "关键件",
    barcodeRules: "条码规则",
    barcodeRulesOf: "{{line}} 的条码规则",
    barcodeRuleCount: "{{n}} 条规则",
    barcodeRulesAdd: "添加规则",
    barcodeRulesNoMaterial: "无物料号",
    importedBarcodeRules: "将按 SN 示例生成 {{n}} 条条码规则",
    importedBarcodeRulesHint:
      "规则按物料号保存，同物料号的各部位及其他 BOM 共用；物料已有规则能匹配示例的，导入时不再重复生成。导入后可在 BOM 页修改。",
    materialCode: "物料编码",
    remark: "备注",
    remarkIn: "备注（{{language}}）",
    remarkLanguage: "备注语言",
    remarkLanguages: { "zh-CN": "中文", "en-US": "English" },
    editRemark: "编辑 {{line}} 的备注",
    expandSlots: "展开 {{line}} 的 {{n}} 个槽位",
    findLine: "搜索位置码、部位名称、物料或备注",
    shownLines: "显示 {{n}} / {{total}} 行",
    noMatchingLines: "没有匹配的行。",
    remarkHint:
      "写给建档扫码的人看，建档时显示在这一行的每个槽位旁边。发布后不可修改。备注按语种分别保存，这里写的是 BOM 页备注列表头所选语言的备注；其他页面按界面语言显示，没有该语言的备注时不显示。",
    addItem: "新增行",
    editItem: "编辑行",
    parent: "父级部位",
    noParent: "—（顶层）",
    quantityHint: "相对父件的用量。整机用量由系统按各级父件相乘算出，不要在这里填整机数量。",
    criticalHint: "关键件按整机用量展开成槽位，建档时每个槽位要扫一个序列号；非关键件只记数量。",
    draftHint:
      "草稿可以编辑。当前 {{critical}} 个关键件行，展开后建档需录 {{slots}} 个序列号。发布后除备注外不可修改。",
    emptyDraft: "这份草稿还没有任何行。点「新增行」开始，或回到列表用文件导入。",
    deleteDraft: "删除这份草稿",
    newDraftHint: "新建一份空草稿，创建后直接进入编辑。草稿要发布之后才能用来建档。",
    emptyForType: "这个设备类型还没有 BOM。点「新建草稿」开始，或导入一个文件。",
    publishedHint:
      "已发布的 BOM 不可修改，只有备注仍可添加和修改。要改其他内容，请派生一个新版本。",
    effectiveHint:
      "整机用量 = 本行用量 × 各级父件用量。关键件按整机用量展开成槽位，每个槽位要录一个 SN。",
    preflightTitle: "导入预检",
    preflightPassed: "预检通过：{{rows}} 行，展开后需录 {{slots}} 个 SN",
    preflightFailed: "预检未通过，共 {{count}} 处问题",
    commitImport: "确认导入为草稿",
    issueRow: "行号",
    issueColumn: "列",
    issueCode: "问题",
    issueValue: "值",
    issueSeq: "序号",
    issueLocation: "安装位置",
    issueMaterialCode: "料号",
    issueDescription: "物料描述",
    importFormat: "格式",
    importFormats: { ENGINEERING: "工程 BOM", TEMPLATE: "位置码模板（旧）" },
    importSheet: "工作表",
    rootMaterialCode: "整机料号",
    suggestedName: "建议名称",
    warningsTitle: "提示 {{count}} 条（不阻止导入）",
    ignoredColumns: "以下列不导入：{{columns}}",
    acknowledgeWarnings: "已核对以上提示",
    engineeringRemark: "工程备注",
    moduleType: "模组",
    moduleTypeOf: "{{line}} 的模组类型",
    notAModule: "不是模组",
    publishModules: "整机建档每台扫 {{scans}} 个码（含 {{modules}} 个模组）；各模组的扫码数如下。",
    modules: {
      allLines: "全部模组",
      robotOnly: "整机直属",
      module: "模组",
      assembly: "总成",
      lines: "行数",
      scans: "建档扫码数",
      robot: "整机",
      serials: "{{n}} 个 SN",
      moduleCodes: "{{n}} 个{{module}}码",
    },
    suggestedModules: "建议的模组（导入后可在草稿中修改）",
  },
  archive: {
    copyLink: "复制档案链接",
    scrapConfirmButton: "确认报废",
    create: "新建档案",
    moduleUpdated: "{{module}} 模组更新到 v{{version}}：",
    causedBy: "由下层变更引起：",
    versionCreated: "建档",
    versionScrapped: "报废",
    versionRecord: "v{{version}} 的维护记录",
    openFullRecord: "打开完整页面",
    addRobot: "新建整机",
    addModule: "新建模组",
    kind: "类型",
    kindRobot: "整机",
    installedModuleReason: "已安装的模组不能报废：先拆下，或随整机报废",
    kindFilter: "类型：{{kind}}",
    moduleState: "模组状态",
    moduleInstalled: "已装",
    kindModule: "模组",
    createTitle: "新建档案",
    foundAt: "命中部位",
    foundBefore: "已换下 · 最后在 v{{version}}",
    searchTitle: "档案查询",
    searchPlaceholder: "档案号 / 设备编号 / 序列号",
    device: "设备编号",
    archiveNo: "档案号",
    productType: "设备类型",
    status: "状态",
    statusActive: "正常",
    statusScrapped: "已报废",
    version: "版本",
    createdAt: "建档时间",
    current: "当前状态",
    versions: "版本历史",
    compare: "版本对比",
    maintenance: "更换记录",
    scrap: "报废",
    scrappedReason: "已报废",
    position: "物料描述",
    materialCode: "物料号",
    serial: "序列号",
    showRows: "显示",
    bomVersion: "BOM 版本",
    findPart: "搜索位置、序列号、物料或备注",
    filter: {
      all: "全部 {{n}}",
      critical: "关键件 {{n}}",
      counted: "非关键件 {{n}}",
    },
    counted: "按数量记录 ×{{n}}",
    quantity: "数量",
    addedByChange: "变更新增",
    addedByChangeHint: "由更换事件新增，BOM 里没有这个部位",
    removed: "已拆除",
    removedHint: "数量为 0：这个部件已经从设备上拆下",
    partScrapped: "已报废",
    partScrappedHint: "档案报废时，这个序列号的物料登记为报废",
    noBom: "无 BOM 建档",
    bomFilter: "按 BOM 筛选：{{version}}",
    searchFilter: "搜索：{{q}}",
    productTypeFilter: "设备类型：{{name}}",
    statusFilter: "状态：{{status}}",
    clearFilter: "清除「{{label}}」",
    offBom: "BOM 外",
    offBomCount: "{{n}} 项 BOM 外物料",
    progress: "已录 {{done}} / 共 {{total}}，还差 {{remaining}} 个",
    complete: "已录齐",
    incompleteHint:
      "关键组件必须全部录入才能提交。缺件的机器现在还不能建档——装齐后再来，或请工艺员派生一版把该件改为非关键。",
    noEntriesYet: "还没有条目。点「新增条目」逐条录入这台设备上的部件。",
    freeEntries: "自定义条目",
    addEntry: "新增条目",
    scanHint: "扫码枪扫描后会自动填入下一个待录槽位。",
    scanPlaceholder: "请录入‘{{remark}}’部序列号",
    locateFirstEmpty: "定位到第一个缺件",
    draftRestored: "已恢复保存的草稿（{{time}}）",
    draftDiscard: "丢弃草稿",
    draftNoDevice: "（未填写设备编号）",
    deviceOptional: "可留空，建档后在档案页补录",
    noDeviceNo: "未填写设备编号",
    fillDeviceNo: "补录设备编号",
    editDeviceNo: "修改设备编号",
    snOptionalPlaceholder: "可选",
    snOptionalHint: "如整机或模组已有 SN 标签，请录入标签上的编号；留空则由系统生成。",
    robotSnLabel: "整机 SN",
    snInvalid: "整机 SN 为 18 位，如 162E1SHA2606300005",
    closeTitle: "关闭新建档案？",
    closeKeepFilling: "继续填写",
    closeConfirm: "关闭",
    closeBody: {
      saved: "已录入的内容已保存为草稿，可在「未完成草稿」中继续填写。",
      nothing: "还没有录入序列号，关闭后不会保存草稿。",
      failed: "草稿保存失败（浏览器存储不可用），关闭后已录入的内容将丢失。",
    },
    drafts: "未完成的草稿（{{n}}）",
    draftsEmpty: "还没有草稿。新建一台设备开始录入。",
    draftsHint:
      "录入内容自动保存为草稿，所有能建档的人都能看到并接着录入（同一台设备只有一份草稿，以最后保存的为准）；提交成功后自动删除。",
    draftEntered: "已录",
    draftSavedTime: "保存时间",
    draftSavedBy: "最后保存人",
    draftSearch: "搜索草稿",
    draftSearchPlaceholder: "设备编号、SN、类型、设备类型或保存人",
    draftsShown: "显示 {{shown}} / {{total}} 份",
    draftsNoMatch: "没有匹配的草稿。",
    draftActions: "操作",
    draftResume: "继续",
    draftResumeOf: "继续录入 {{device}}",
    draftDeleteOf: "删除 {{device}} 的草稿",
    draftDeleteTitle: "删除草稿？",
    draftDeleteBody: "{{device}} 的草稿（已录入 {{n}} 项）将被删除，此操作不可撤销。",
    draftDiscardBody: "本草稿已录入的 {{n}} 项将被清空并删除，此操作不可撤销。",
    draftSaved: "已自动保存为草稿（{{time}}）",
    scrapConfirm: "报废会释放这台设备上全部 SN 占用，使它们可以登记到别的设备上。此操作不可撤销。",
    scrapReason: "报废原因",
    scrapReleased: "已释放 {{count}} 个序列号",
    chooseProductType:
      "请先选择设备类型。已配置 BOM 的设备类型，按 BOM 条目录入组件序列号（支持扫码）；未配置 BOM 的设备类型，可添加自定义条目。",
    duplicateSerials: "同一台设备上序列号重复：{{serials}}",
    installedTitle: "{{n}} 个序列号已安装在其他设备上：",
    installedAt:
      "{{serial}} 已安装在设备 {{deviceNo}}（{{productType}}）的「{{position}}」上，安装于 {{time}}，档案",
    installedShort: "已安装在 {{archiveNo}}「{{position}}」",
    installedBlocksSubmit: "有序列号已安装在其他设备上，换成未安装的件后才能提交。",
    scan: {
      duplicate: "序列号 {{serial}} 已经录在这台设备的另一个槽位上",
      "ignored-repeat": "已忽略重复扫描（{{serial}}）",
      placeholder: "这不是一个序列号，槽位未填写",
      "no-empty-slot": "所有槽位都已录满",
      "format-mismatch":
        "{{serial}} 不符合「{{position}}」物料 {{material}} 的条码规则（{{patterns}}），未录入",
    },
  },
  scrap: {
    modulesTitle: "装在上面的模组去向",
    fateOf: "{{archiveNo}} 的去向",
    release: "拆下入库",
    scrapWith: "随之报废",
    staysInside: "其中的 {{n}} 个下层模组随它一起入库",
    partsTitle: "录入了序列号的物料去处（{{n}} 个）",
    partsHint:
      "每个物料都会记录去处。重新入库的可以装到其他设备上；报废的物料以后再录入时会提示已报废。",
    partFate: "去处",
    partFateOf: "{{position}} 的去处",
    restocked: "重新入库",
    scrapped: "报废",
    allRestocked: "全部重新入库",
    allScrapped: "全部报废",
    partsRecorded: "已记录物料去处：报废 {{scrapped}} 个，重新入库 {{restocked}} 个。",
  },
  scrappedSerial: {
    title: "该物料已报废",
    body: "序列号 {{serial}} 的物料已报废：曾装在 {{archiveNo}}「{{position}}」，{{date}} 随档案报废时登记为报废。",
    question: "报废的物料不应再装机。确认要录入吗？",
    remove: "移除这个序列号",
    keep: "仍然录入",
  },
  moduleArchive: {
    location: "当前位置",
    inStock: "在库",
    installations: "安装履历",
    neverInstalled: "还没有装到任何整机或模组上",
    parent: "装在",
    installedAt: "装上时间",
    removedAt: "拆下时间",
    parentVersions: "上层版本",
    stillInstalled: "仍在装",
    showContents: "展开 {{archiveNo}} 的内容",
    hideContents: "收起 {{archiveNo}} 的内容",
  },
  moduleFiling: {
    title: "新建模组档案",
    nestedTitle: "现场建档：{{position}}",
    moduleType: "模组类型",
    noModules: "这个 BOM 没有标记模组",
    snLabel: "模组 SN",
    filedOnSpot: "刚建档的模组 {{archiveNo}} 的 SN",
    draftNoSn: "（提交后生成 SN）",
    snInvalid: "模组 SN 为 14 位，第 4 位是所选类型「{{type}}」的字母，如 150W2610230001",
    slotsTitle: "模组",
    slotsProgress: "已装入 {{done}} / {{total}}",
    code: "模组码",
    state: "状态",
    waiting: "待扫码",
    checking: "查询中…",
    duplicate: "{{archiveNo}} 已经装在本次建档的另一个模组位上",
    notFound: "该模组还没有建档",
    fileHere: "现场建档",
    lookupFailed: "查询失败，请重试",
    problem: {
      not_active: "该模组已报废，不能装入",
      type_mismatch: "这是{{actual}}模组，此处应扫{{expected}}",
      product_type_mismatch: "该模组属于其他设备类型，不能装入",
      bom_mismatch: "该模组的 BOM 版本是 {{theirs}}；建档时模组须与本次建档同一 BOM 版本",
      installed: "已装于 {{where}}，不能装入",
      incomplete: "该模组还缺下层模组（{{slots}}），请先补齐",
    },
    scan: {
      "module-no-slot": "没有空的 {{module}} 模组位可以装入",
      "robot-sn": "这是机器人 SN，不是模组码或零件序列号",
    },
  },
  maintenance: {
    serialBlocked: "有序列号已安装在其他位置或重复填写，改正后才能提交",
    formatBlocked: "有序列号不符合物料的条码规则，改正后才能提交",
    moduleBlocked: "有模组不能装入所填的模组位，改正后才能提交",
    moduleBomChanged: "注意：新模组 BOM 版本 {{after}}，与原模组 {{before}} 不一致",
    moduleBomChangedSummary: "注意：{{n}} 个模组的 BOM 版本与原模组不一致",
    moduleBomNone: "（无版本）",
    moduleFits: "可装入：{{archiveNo}}",
    moduleDuplicate: "{{archiveNo}} 填在了两个模组位上",
    jiraUrl: "Jira 链接",
    jiraUrlPlaceholder: "https://…/browse/SUP-123",
    jiraUrlHint: "选填：粘贴这次变更对应的 Jira ticket 链接，维护记录里可以直接打开",
    jiraUrlInvalid: "请填写以 http:// 或 https:// 开头的完整链接，或留空",
    jiraOpen: "Jira",
    pendingCorrection:
      "{{archive}}维护记录 #{{n}} 还在缓冲期（至 {{time}}）。提交这次变更后，它将不能再编辑或撤销；如果它有错，请先改正它。",
    pendingCorrectionOpen: "打开维护记录 #{{n}}",
    leaveTitle: "放弃本次变更？",
    leaveBody: "已填写的内容（勾选或新增 {{n}} 项）尚未提交，记录变更不保存草稿，离开后将丢失。",
    leaveKeepEditing: "继续编辑",
    leaveDiscard: "放弃并离开",
    pick: "选择",
    filterSelected: "已选 {{n}}",
    newMaterialCode: "新物料号",
    newMaterialName: "新物料描述",
    fittedModule: "装入的模组",
    newModulePlaceholder: "扫新模组的 SN 或档案号",
    thisArchive: "本档案",
    archiveSummary: "{{archive}}：更换 {{replaced}}，拆除 {{removed}}",
    moduleConflict: "同一次提交不能既换下或拆下模组，又改它内部的件",
    affected: "已生成新版本：{{list}}",
    fittedSerial: "新序列号",
    substituteNeedsBoth: "使用替代物料时，新物料号和新物料描述都必须填写。",
    useSubstitute: "使用替代物料",
    substituteHint: "仅在实际装上的物料与 BOM 指定物料不同时填写",
    substituteHintNoBom: "仅在物料变更时填写",
    replaceQuantity: "更换数量：",
    removeQuantity: "拆除数量：",
    currentQuantity: "，当前数量：{{n}}",
    newSerialPlaceholder: "扫码或输入",
    groupBadge: "组件 · 含 {{n}} 个子部件",
    groupHint: "这一行本身是可更换的组件；勾选它只记录它自己的变更，其下 {{n}} 个子部件不随之变更",
    pickedBelow: "已选 {{n}}",
    groupNote:
      "只记录「{{name}}」本身的变更，其下 {{n}} 个子部件不随之变更；子部件如有更换，请分别勾选。",
    selectedSummary: "已选 {{n}} 项：更换 {{replaced}} 项，拆除 {{removed}} 项，新增 {{added}} 项",
    submit: "提交变更",
    stepReason: "1. 变更类型与说明",
    stepParts: "2. 选择部件",
    partsStepPick: "勾选需要变更的部件后填写。",
    partsStepFill:
      "更换：关键件填写新序列号（可扫码），非关键件填写更换数量；拆除：无需填写新部件信息。",
    partsStepAdd: "未列入 BOM 的部件：点击上方「添加 BOM 外部件」。",
    partsStepAddNoBom: "新部件：点击上方「添加新部件」。",
    currentlyFitted: "当前部件",
    findPart: "按部位、物料号、序列号或备注搜索",
    shownParts: "显示 {{n}} 条，共 {{total}} 条",
    addOutOfBom: "添加 BOM 外部件",
    addOutOfBomNoBom: "添加新部件",
    replaceNeedsSerial: "更换必须填写换上去的序列号，否则等于没记录换了什么。",
    none: "这台设备还没有更换记录。",
    actions: "操作",
    open: "打开",
    correct: "编辑",
    revoke: "撤销",
    correctableFor: "缓冲期至 {{time}}（剩 {{minutes}} 分钟）",
    correctableHint:
      "提交后 2 小时内、且之后没有新的变更时，可以编辑或撤销这条记录；过了缓冲期记录生效，如有错误需新增一条维护记录",
    correctTitle: "编辑维护记录 #{{n}}",
    saveCorrection: "保存修改",
    correctUnsupported: "这条记录含有此处无法编辑的变更，只能撤销后重新录入",
    revokeTitle: "撤销维护记录 #{{n}}？",
    revokeBody:
      "撤销后，相关设备恢复到这次变更之前的状态，这条记录从列表中移除（审计日志中保留）。变更：{{reason}}",
    openRecord: "打开维护记录 #{{n}}",
    copyLink: "复制维护记录链接",
    backToList: "← 全部维护记录",
    eventTitle: "维护记录 #{{n}}",
    title: "更换记录",
    start: "记录变更",
    action: "操作",
    replace: "更换",
    add: "加装",
    remove: "拆除",
    oldSerial: "换下序列号",
    newSerial: "换上序列号",
    newSerialOrCount: "新序列号 / 数量",
    newMaterial: "新物料（与 BOM 指定物料不同时填写）",
    newMaterialNoBom: "新物料（物料变更时填写）",
    reason: "变更说明",
    reasonPlaceholder: "选填；类型为「其他」时必填",
    changeType: "变更类型",
    changeTypeChoose: "请选择",
    changeColumn: "变更类型 / 说明",
    changeTypes: {
      REPAIR: "故障维修",
      PREVENTIVE: "预防性维护",
      UPGRADE: "升级改造",
      ASSEMBLY: "装配调整",
      QUALITY: "质量问题",
      OTHER: "其他",
    },
    occurredAt: "发生时间",
    occurredAtHint: "留空不改就按提交时间记录",
    occurredBeforeFiling: "不能早于建档时间 {{time}}",
    occurredInFuture: "不能晚于当前时间（{{time}}）",
    occurredBeforeModule:
      "发生时间早于 {{archive}} 的建档时间（{{time}}），这次变更会改到它：请回第 1 步修改发生时间，或留空",
    occurredBeforeLast: "早于上一次变更（{{time}}），将作为补录保存，在更换记录中排在最前",
    newPosition: "BOM 外物料描述",
    newPositionNoBom: "物料描述",
    addLine: "新增明细",
    incompleteLines:
      "第 {{lines}} 行还没填完：更换和拆除要选部位，更换还要填换上的序列号，加装要选部位或填新物料的物料描述。",
    sequence: "序号",
    expandAll: "全部展开",
    collapseAll: "全部折叠",
    submittedAt: "提交时间",
    submittedBy: "提交人",
    unchangedBadge: "仅数量变更",
    scrapBadge: "报废",
    identicalHint: "两个版本的装配内容完全相同——变化在下面的更换记录里。",
    filterPosition: "按部位筛选",
    filterAction: "按操作筛选",
  },
  table: {
    resizeColumn: "调整第 {{n}} 列宽度",
    resizeHint: "拖动调整列宽，双击恢复自动宽度",
  },
  tree: {
    levels: "层级",
    expandAll: "全部展开",
    collapseAll: "全部折叠",
    expand: "展开 {{name}}",
    collapse: "折叠 {{name}}",
    disabledWhileFiltered: "筛选或搜索时显示全部匹配行，不能折叠",
    slots: "{{n}} 个槽位",
  },
  serialStatus: {
    checking: "校验中…",
    free: "未使用，可安装",
    usedRemoved: "曾装在 {{archiveNo}}「{{position}}」，已拆下（截至 v{{version}}）",
    usedScrapped: "曾装在 {{archiveNo}}「{{position}}」，随档案报废",
    usedPartScrapped: "曾装在 {{archiveNo}}「{{position}}」，档案报废时该物料已报废",
    usedPartRestocked: "曾装在 {{archiveNo}}「{{position}}」，档案报废时已拆下重新入库",
    swap: "本次从「{{position}}」换下，可互换",
    duplicate: "本次已在其他位置填写了这个序列号",
    formatMismatch: "不符合物料 {{material}} 的条码规则：{{patterns}}",
  },
  robotSn: {
    print: "打印标签",
    printProdOnly: "只有正式环境可以打印标签：测试环境生成的 SN 可能与正式环境重复",
    testMark: "测试",
    testOnly: "测试环境生成的 SN，仅供测试，不能用于实物",
    label: "SN",
    qrLabel: "SN {{sn}} 的二维码",
    barcodeLabel: "SN {{sn}} 的条码",
    copy: "复制 SN",
    typeCode: "SN 类型码",
    typeCodeHint:
      "SN 第 4–5 位，2 位大写字母或数字；默认取档案号前缀首字母 + 1（已被占用则顺延），创建后不可修改",
  },
  position: {
    replace: "更换此部件",
    swap: "与其他槽位互换",
    scrapped: "已报废的档案不能变更",
    change: "变更此部件",
    nothingFitted: "已全部拆除，没有可变更的数量",
    swapWith: "互换的槽位",
    swapPreview:
      "提交后「{{here}}」装 {{thereSerial}}，「{{there}}」装 {{hereSerial}}，记为一次变更。",
    useSwap: "改为与本机「{{position}}」互换",
    history: "变更历史",
    filed: "建档",
    noHistory: "还没有记录。",
    open: "查看 {{position}} 的变更历史",
    currentSerial: "当前序列号",
    materialCode: "物料编码",
    copySerial: "复制序列号",
    copyMaterialCode: "复制物料编码",
    newSerial: "新部件序列号",
    newSerialPlaceholder: "扫码或输入序列号",
    material: "物料",
    useSubstitute: "使用替代物料",
    keepMaterial: "沿用物料编码：{{code}}",
    reason: "更换说明",
    occurredAt: "更换时间",
    occurredAtHint: "留空时，以提交时间作为更换时间",
    save: "保存更换记录",
    missing: "还不能保存，请填写：{{fields}}",
    listSeparator: "、",
    saved: "更换记录已保存（版本 v{{version}}）。",
    action: "变更方式",
    quantity: "数量",
    quantityHint: "当前数量：{{n}}",
    currentQuantity: "当前数量",
    changeReason: "变更说明",
    changeOccurredAt: "变更时间",
    changeOccurredAtHint: "留空时，以提交时间作为变更时间",
    changeSave: "保存变更记录",
    changeSaved: "变更记录已保存（版本 v{{version}}）。",
  },
  datetime: {
    timeZone: "时区：{{zone}}（{{offset}}）",
    placeholder: "年-月-日 时:分，如 2026-10-13 14:30",
    open: "选择日期和时间",
    previousMonth: "上个月",
    nextMonth: "下个月",
    hour: "时",
    minute: "分",
    now: "现在",
    clear: "清除",
    done: "完成",
    invalid: "日期时间无效，请按 2026-10-13 14:30 的格式填写",
  },
  compare: {
    fromVersion: "起始版本",
    toVersion: "目标版本",
    modules: "模组",
    moduleSlot: "模组位",
    moduleSwapped: "更换模组",
    moduleUpdated: "模组内部变更",
    moduleBomChanged: "BOM 版本不同",
    pinnedVersion: "档案 v{{version}}",
    bomVersion: "BOM {{version}}",
    noBomVersion: "（无版本）",
    moduleBomSummary: "{{count}} 个模组位换装了不同 BOM 版本的模组：{{positions}}",
    emptySlot: "（空）",
    added: "新增",
    removed: "移除",
    changed: "变更",
    before: "变更前",
    after: "变更后",
    newMaterial: "新物料",
    newMaterialSummary: "{{count}} 个部位换用了新物料：{{positions}}",
  },
  system: {
    title: "系统信息",
    language: "界面语言",
    languageSwitch: "中文界面",
    account: "当前账号",
    sessionExpires: "会话到期时间",
    sessionHint: "会话自登录起 30 天到期，使用不会延长。",
    clientVersion: "前端版本",
    minVersion: "服务端要求的最低版本",
    serverTime: "服务端时间",
  },
  audit: {
    title: "审计日志",
    actor: "操作人",
    actorEmail: "操作人邮箱",
    action: "动作",
    entity: "对象",
    entityId: "对象标识（ID）",
    occurredAt: "时间",
    allEntityTypes: "全部对象类型",
    details: "详情",
    requestId: "请求标识",
    before: "变更前",
    after: "变更后",
    entityTypes: {
      device_archive: "设备档案",
      maintenance_event: "维修记录",
      bom: "BOM",
      product_type: "设备类型",
      robot_sn: "机器人 SN",
      bom_import_batch: "BOM 导入",
      archive_import_batch: "档案导入",
      app_user: "用户",
      user_session: "登录会话",
    },
    actions: {
      auth_login_succeeded: "登录",
      auth_login_rejected: "登录被拒",
      auth_logout: "退出登录",
      bom_import_preflight: "BOM 导入预检",
      bom_import_committed: "BOM 导入提交",
      archive_import_preflight: "档案导入预检",
      archive_import_committed: "档案导入提交",
      archive_created: "建档",
      archive_batch_created: "批量建档",
      archive_scrapped: "报废",
      maintenance_submitted: "提交维修",
      product_type_created: "新建型号",
      product_type_discontinued: "停用型号",
      product_type_activated: "启用型号",
      product_type_deleted: "删除型号",
      product_type_sn_type_code_set: "设置 SN 类型码",
      bom_created: "新建 BOM",
      bom_published: "发布 BOM",
      bom_derived: "派生 BOM",
      bom_deleted: "删除 BOM",
      bom_disabled: "禁用 BOM",
      bom_enabled: "启用 BOM",
      bom_item_changed: "修改 BOM 物料",
      bom_critical_marked: "标记关键件",
      robot_sn_reserved: "预留 SN",
    },
  },
  conflict: {
    title: "档案已被其他操作更新",
    body: "你打开时是第 {{base}} 版，现在已经是第 {{latest}} 版。你填的内容保留着，但不能直接提交——请先看看最新状态，再决定原来的操作是否仍然适用。",
    openLatest: "在新标签查看最新版本",
    refresh: "刷新后重新确认",
  },
  error: {
    field: {
      string_pattern_mismatch: "格式不符合要求",
      string_too_short: "太短",
      string_too_long: "太长",
      missing: "必填",
      uuid_parsing: "不是有效的标识",
      int_parsing: "必须是整数",
      greater_than_equal: "小于允许的最小值",
      datetime_parsing: "不是有效的时间",
      bool_parsing: "必须是是/否",
    },
    title: "出错了",
    requestId: "请求标识：{{id}}",
    clockSkew: "本机时间 {{client}}，服务器时间 {{server}}，相差约 {{minutes}} 分钟",
    timeBeforeFiling:
      "发生时间 {{occurred}} 早于 {{archive}} 的建档时间 {{created}}。请改为建档之后的时间，或留空按提交时间记录。",
    timeInFuture:
      "发生时间 {{occurred}} 比服务器时间 {{server}} 晚约 {{minutes}} 分钟。请检查电脑时钟是否准确，或把发生时间留空。",
    serialFormat: "{{position}}：{{serial}} 不符合物料 {{material}} 的条码规则（{{patterns}}）",
    network: "网络请求失败，请检查连接后重试",
  },
} as const;
